import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DsButton } from '../ds';
import { AlertIcon } from '../ds/icons';
import { TOAST_SURFACE } from '../ds/toast';
import {
  ApiV2Error,
  AppSource,
  deployUpload,
  getAppSource,
} from '../../utils/apiV2';
import { formatDateTime } from '../../utils/dates';
import UploadSource, { UploadSourceValue } from './UploadSource';

/**
 * Settings > Build & deploy > Source, for an app that deploys from uploads:
 * the code it has now, a way to it (the Source Code screen), and a way to
 * send new code — which is a deploy.
 *
 * Kept honest about what this is: the platform keeps the live code and the
 * one before it, for rollback. It is not a backup, and says so.
 */

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const retentionLine = (pinnedAs: 'CURRENT' | 'PREVIOUS' | null) =>
  pinnedAs === 'CURRENT'
    ? 'kept while live or previous, not a backup'
    : pinnedAs === 'PREVIOUS'
    ? 'previous deploy, kept for rollback'
    : 'not live yet';

const errorMessage = (e: unknown, fallback: string) =>
  e instanceof ApiV2Error && e.status > 0 && e.status < 500
    ? e.nextAction || e.message
    : fallback;

interface UploadSourceCardProps {
  appId: string;
  canWrite: boolean;
  /** After new code was sent and its deployment started. */
  onDeployed?: (deploymentId: string) => void;
}

const UploadSourceCard = ({ appId, canWrite, onDeployed }: UploadSourceCardProps) => {
  const [source, setSource] = useState<AppSource | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadValue, setUploadValue] = useState<UploadSourceValue | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    getAppSource(appId)
      .then(setSource)
      .catch((e) => setLoadError(errorMessage(e, "This app's code could not be loaded.")));
  }, [appId]);

  useEffect(load, [load]);

  const latest = source?.latest;
  // The upload's short id: how a deploy's code is told apart at a glance.
  const shortHash = latest ? latest.manifestSha256.slice(0, 7) : '';

  const onUploaded = async ({ uploadId, manifestSha256, sentCount }: { uploadId: string; manifestSha256: string; sentCount: number }) => {
    try {
      const { deploymentId } = await deployUpload(appId, uploadId, manifestSha256);
      toast.success(
        sentCount ? `Deploying new code · ${sentCount} ${sentCount === 1 ? 'file' : 'files'} changed` : 'Deploying · no files changed',
        { className: TOAST_SURFACE }
      );
      setIsUploadOpen(false);
      setUploadValue(null);
      load();
      onDeployed?.(deploymentId);
    } catch (e) {
      toast.error(errorMessage(e, 'The new code could not be deployed.'), { className: TOAST_SURFACE });
      // Sent, but not deployed: picking the same folder again reuses it.
    }
  };

  return (
    <div>
      <div className="rounded-card border border-navy-500 bg-navy-800">
        <div className="flex items-center gap-4 px-5 py-4 [@media(max-width:639.98px)]:flex-col [@media(max-width:639.98px)]:items-start">
          <div className="flex-1 min-w-0">
            {loadError ? (
              <div role="alert" className="flex items-center gap-2 text-ds-125 text-state-err">
                <AlertIcon size={14} /> {loadError}
              </div>
            ) : !source ? (
              <div className="font-courrier text-ds-115 text-navy-200">Loading code…</div>
            ) : latest ? (
              <>
                <div className="text-ds-135 font-semibold text-white truncate">
                  Latest code · Upload {shortHash}
                </div>
                <div className="mt-1 text-ds-125 text-navy-200">
                  {latest.fileCount.toLocaleString()} {latest.fileCount === 1 ? 'file' : 'files'}, {formatBytes(latest.totalBytes)} ·
                  {' '}uploaded {formatDateTime(new Date(latest.uploadedAt))} · {retentionLine(latest.pinnedAs)}
                </div>
              </>
            ) : (
              <div className="text-ds-135 font-semibold text-white">No code uploaded yet</div>
            )}
          </div>
          <div className="flex flex-none gap-2">
            {latest ? (
              <DsButton variant="outline" size="md" onClick={() => navigate(`/apps/${appId}/source`)}>
                View files
              </DsButton>
            ) : null}
            {canWrite && source && !isUploadOpen ? (
              <DsButton variant="action" size="md" onClick={() => setIsUploadOpen(true)}>
                Upload new code
              </DsButton>
            ) : null}
          </div>
        </div>

        {/* Opens in the same box: new code replaces what the row above shows. */}
        {isUploadOpen ? (
          <div className="border-t border-navy-500 px-5 py-4">
            <UploadSource
              appId={appId}
              compact
              value={uploadValue}
              onChange={setUploadValue}
              onNext={onUploaded}
              nextLabel="Upload and deploy"
            />
            <div className="mt-2">
              <DsButton
                variant="ghost"
                size="md"
                onClick={() => {
                  setIsUploadOpen(false);
                  setUploadValue(null);
                }}
              >
                Cancel
              </DsButton>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UploadSourceCard;
